import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { LifeBuoy, Plus, ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../stores/authStore'
import {
  useSupportTicketsStore,
  STATUS_LABELS,
  CATEGORY_LABELS,
  CATEGORY_KEYS,
  categoryLabel,
  type SupportTicket,
  type TicketStatus,
  type TicketCategory,
} from '../stores/supportTicketsStore'

// Internes Support-/Ticket-System.
//
// Beim Erfassen wird eine Kategorie gewählt; die Meldung geht per E-Mail an das
// Postfach dieses Kategorien (Empfänger wird serverseitig aufgelöst, siehe
// netlify/functions/send-email.ts). Bearbeitet wird das Ticket hier.
//
// Sichtbarkeit steuert die Datenbank (RLS), nicht diese Seite: ein Trainer
// bekommt beim Laden nur seine eigenen Tickets zurück, ein Admin alle. Die
// Admin-Sektion unten ist reine Darstellung des ohnehin Geladenen.
//
// Tickets aus der Zeit vor der Kategorie-Einführung — und solche von Geräten mit
// veraltetem Service Worker — haben keine Kategorie. Die werden als "Ohne
// Kategorie" geführt und sind über den Filter auffindbar, damit sie nicht
// unbemerkt liegenbleiben.

const GREEN = '#1B7E3A'
const STATUSES: TicketStatus[] = ['open', 'in_progress', 'done']

const STATUS_STYLE: Record<TicketStatus, { bg: string; fg: string }> = {
  open: { bg: '#fef3c7', fg: '#92400e' },
  in_progress: { bg: '#dbeafe', fg: '#1e40af' },
  done: { bg: '#dcfce7', fg: '#166534' },
}

const card = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 14,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)',
}

const inputStyle = {
  padding: '10px 12px',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  fontSize: 15,
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box' as const,
}

const labelStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: 6,
  fontSize: 14,
  fontWeight: 600,
  color: '#374151',
}

function StatusBadge({ status }: { status: TicketStatus }) {
  const s = STATUS_STYLE[status]
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {STATUS_LABELS[status]}
    </span>
  )
}

// Kategorie-Kennzeichnung. Tickets ohne Kategorie bekommen bewusst eine auffällige
// Darstellung — sie sind der Hinweis darauf, dass ein Gerät noch mit einem
// alten Stand der App arbeitet, und müssen von Hand zugeordnet werden.
function CategoryBadge({ category }: { category: string | null }) {
  const missing = !category
  return (
    <span
      style={{
        background: missing ? '#fee2e2' : '#eef2ff',
        color: missing ? '#991b1b' : '#3730a3',
        padding: '3px 10px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
      title={missing ? 'Keine Kategorie hinterlegt — es ging keine Benachrichtigung raus' : undefined}
    >
      {categoryLabel(category)}
    </span>
  )
}

function TicketRow({
  ticket,
  expanded,
  onToggle,
  canEdit,
  canDelete,
  ownerLabel,
}: {
  ticket: SupportTicket
  expanded: boolean
  onToggle: () => void
  canEdit: boolean
  canDelete: boolean
  ownerLabel?: string
}) {
  const { updateTicket, deleteTicket } = useSupportTicketsStore()
  const [body, setBody] = useState(ticket.body)
  const [status, setStatus] = useState<TicketStatus>(ticket.status)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  // Wenn der Store nach einem Speichern neu lädt, lokale Felder nachziehen.
  useEffect(() => {
    setBody(ticket.body)
    setStatus(ticket.status)
  }, [ticket.body, ticket.status])

  const dirty = body !== ticket.body || status !== ticket.status

  const save = async () => {
    setErr(null)
    setMsg(null)
    setBusy(true)
    const res = await updateTicket(ticket.id, { body, status })
    setBusy(false)
    if (res.error) setErr(res.error)
    else setMsg('Gespeichert.')
  }

  const remove = async () => {
    if (!window.confirm('Ticket endgültig löschen?')) return
    setErr(null)
    setBusy(true)
    const res = await deleteTicket(ticket.id)
    setBusy(false)
    if (res.error) setErr(res.error)
  }

  return (
    <li style={{ ...card, marginBottom: 10, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '14px 16px', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left', font: 'inherit',
        }}
      >
        {expanded ? <ChevronDown size={18} color="#6b7280" /> : <ChevronRight size={18} color="#6b7280" />}
        <span style={{ flex: 1, fontWeight: 600, color: '#111', minWidth: 120 }}>{ticket.title}</span>
        {ownerLabel && <span style={{ fontSize: 13, color: '#6b7280' }}>{ownerLabel}</span>}
        <CategoryBadge category={ticket.category} />
        <StatusBadge status={ticket.status} />
      </button>

      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f3f4f6' }}>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '12px 0' }}>
            Erstellt {new Date(ticket.created_at).toLocaleString('de-CH')} · Kategorie: {categoryLabel(ticket.category)}
            {!ticket.category && ' — für dieses Ticket ging keine Benachrichtigung raus'}
          </p>

          {canEdit ? (
            <>
              <label style={{ ...labelStyle, marginBottom: 12 }}>
                Beschreibung
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                <label style={{ ...labelStyle, flex: '0 1 200px' }}>
                  Status
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TicketStatus)}
                    style={inputStyle}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy || !dirty}
                  style={{
                    padding: '11px 18px', background: GREEN, color: '#fff', border: 'none',
                    borderRadius: 10, fontSize: 15, fontWeight: 700,
                    cursor: busy || !dirty ? 'not-allowed' : 'pointer', opacity: busy || !dirty ? 0.5 : 1,
                  }}
                >
                  {busy ? 'Speichere…' : 'Speichern'}
                </button>
                {canDelete && (
                  <button
                    type="button"
                    onClick={remove}
                    disabled={busy}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 14px',
                      background: '#fff', color: '#b91c1c', border: '1px solid #fecaca',
                      borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <Trash2 size={16} /> Löschen
                  </button>
                )}
              </div>
            </>
          ) : (
            <p style={{ whiteSpace: 'pre-wrap', color: '#374151', margin: 0 }}>
              {ticket.body || <em style={{ color: '#9ca3af' }}>Keine Beschreibung</em>}
            </p>
          )}

          {msg && <p style={{ color: GREEN, fontWeight: 600, margin: '12px 0 0' }}>✓ {msg}</p>}
          {err && <p style={{ color: '#b91c1c', fontWeight: 600, margin: '12px 0 0' }}>{err}</p>}
        </div>
      )}
    </li>
  )
}

export default function SupportPage() {
  const { tickets, loading, error, fetchTickets, addTicket } = useSupportTicketsStore()
  const { user, isAdmin } = useAuthStore()
  const admin = isAdmin()

  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [category, setCategory] = useState<TicketCategory | ''>('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [warn, setWarn] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const [openMine, setOpenMine] = useState<string | null>(null)
  const [openAll, setOpenAll] = useState<string | null>(null)
  const [filter, setFilter] = useState<TicketStatus | 'all'>('open')
  const [catFilter, setCatFilter] = useState<TicketCategory | 'all' | 'none'>('all')
  const [owners, setOwners] = useState<Record<string, string>>({})

  useEffect(() => {
    void fetchTickets()
  }, [fetchTickets])

  // Für die Admin-Liste die Namen zu den owner_ids holen. support_tickets hat
  // nur einen FK auf auth.users, deshalb kein Join — separat laden und mappen.
  useEffect(() => {
    if (!admin) return
    let cancelled = false
    void (async () => {
      const { data } = await supabase.from('profiles').select('id, name, email')
      if (cancelled || !data) return
      const map: Record<string, string> = {}
      for (const p of data) map[p.id] = p.name || p.email || p.id.slice(0, 8)
      setOwners(map)
    })()
    return () => { cancelled = true }
  }, [admin])

  const myTickets = useMemo(
    () => tickets.filter((t) => t.owner_id === user?.id),
    [tickets, user?.id]
  )

  const allFiltered = useMemo(
    () =>
      tickets
        .filter((t) => (filter === 'all' ? true : t.status === filter))
        .filter((t) =>
          catFilter === 'all' ? true : catFilter === 'none' ? !t.category : t.category === catFilter
        ),
    [tickets, filter, catFilter]
  )

  // Zähler für den Hinweis auf Tickets ohne Kategorie — die entstehen durch
  // Geräte mit veraltetem Stand der App und lösen keine Benachrichtigung aus.
  const withoutCategory = useMemo(() => tickets.filter((t) => !t.category).length, [tickets])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setMsg(null)
    setWarn(null)
    setErr(null)
    if (!category) {
      setErr('Bitte eine Kategorie auswählen.')
      return
    }
    setBusy(true)
    const res = await addTicket(title, body, category)
    setBusy(false)
    if (res.error) setErr(res.error)
    else {
      setMsg(res.mailWarning ? 'Ticket erstellt.' : 'Ticket erstellt und an die Kategorie gemeldet.')
      if (res.mailWarning) setWarn(res.mailWarning)
      setTitle('')
      setBody('')
      setCategory('')
    }
  }

  return (
    <div style={{ padding: '24px 0', maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 28, fontWeight: 800, color: '#111', margin: '0 0 4px' }}>
        <LifeBuoy size={26} color={GREEN} /> Support
      </h1>
      <p style={{ color: '#555', margin: '0 0 24px' }}>
        Problem oder Frage? Kategorie wählen und Ticket erfassen — die Meldung geht an das zuständige
        Postfach und kann hier weiterverfolgt werden.
      </p>

      {/* Neues Ticket */}
      <form onSubmit={submit} style={{ ...card, padding: 20, marginBottom: 28 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={labelStyle}>
            Kategorie
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as TicketCategory | '')}
              required
              style={inputStyle}
            >
              <option value="">Bitte wählen …</option>
              {CATEGORY_KEYS.map((k) => (
                <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
              ))}
            </select>
          </label>
          <label style={labelStyle}>
            Titel
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="Kurz: worum geht es?"
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Beschreibung
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              placeholder="Was ist passiert? Was hast du erwartet?"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </label>
          <div>
            <button
              type="submit"
              disabled={busy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px',
                background: GREEN, color: '#fff', border: 'none', borderRadius: 10,
                fontSize: 15, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.6 : 1,
              }}
            >
              <Plus size={18} /> {busy ? 'Erstelle…' : 'Ticket erstellen'}
            </button>
          </div>
        </div>
        {msg && <p style={{ color: GREEN, fontWeight: 600, margin: '14px 0 0' }}>✓ {msg}</p>}
        {warn && <p style={{ color: '#92400e', fontWeight: 600, margin: '8px 0 0' }}>⚠ {warn}</p>}
        {err && <p style={{ color: '#b91c1c', fontWeight: 600, margin: '14px 0 0' }}>{err}</p>}
      </form>

      {error && <p style={{ color: '#b91c1c', fontWeight: 600 }}>{error}</p>}

      {/* Meine Tickets */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 12px' }}>
        Meine Tickets {myTickets.length > 0 && <span style={{ color: '#6b7280', fontWeight: 500 }}>({myTickets.length})</span>}
      </h2>
      {loading && myTickets.length === 0 ? (
        <p style={{ color: '#6b7280' }}>Lade…</p>
      ) : myTickets.length === 0 ? (
        <p style={{ color: '#6b7280', marginBottom: 28 }}>Du hast noch keine Tickets erfasst.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px' }}>
          {myTickets.map((t) => (
            <TicketRow
              key={t.id}
              ticket={t}
              expanded={openMine === t.id}
              onToggle={() => setOpenMine(openMine === t.id ? null : t.id)}
              canEdit
              canDelete={admin}
            />
          ))}
        </ul>
      )}

      {/* Alle Tickets — nur Admin */}
      {admin && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, margin: '0 0 12px' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: '#111', margin: 0 }}>
              Alle Tickets <span style={{ color: '#6b7280', fontWeight: 500 }}>({allFiltered.length})</span>
            </h2>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as TicketStatus | 'all')}
              style={{ ...inputStyle, width: 'auto', padding: '7px 10px', fontSize: 14 }}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
              <option value="all">Alle Status</option>
            </select>
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value as TicketCategory | 'all' | 'none')}
              style={{ ...inputStyle, width: 'auto', padding: '7px 10px', fontSize: 14 }}
            >
              <option value="all">Alle Kategorien</option>
              {CATEGORY_KEYS.map((k) => (
                <option key={k} value={k}>{CATEGORY_LABELS[k]}</option>
              ))}
              <option value="none">Ohne Kategorie{withoutCategory > 0 ? ` (${withoutCategory})` : ''}</option>
            </select>
          </div>
          {withoutCategory > 0 && catFilter !== 'none' && (
            <p style={{ fontSize: 13, color: '#92400e', margin: '0 0 12px' }}>
              {withoutCategory === 1
                ? '1 Ticket hat keine Kategorie und hat deshalb keine Benachrichtigung ausgelöst.'
                : `${withoutCategory} Tickets haben keine Kategorie und haben deshalb keine Benachrichtigung ausgelöst.`}{' '}
              Über den Kategoriefilter «Ohne Kategorie» sichtbar.
            </p>
          )}
          {allFiltered.length === 0 ? (
            <p style={{ color: '#6b7280' }}>Keine Tickets in dieser Ansicht.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {allFiltered.map((t) => (
                <TicketRow
                  key={t.id}
                  ticket={t}
                  expanded={openAll === t.id}
                  onToggle={() => setOpenAll(openAll === t.id ? null : t.id)}
                  canEdit
                  canDelete
                  ownerLabel={owners[t.owner_id]}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
